import { FC, useEffect, useMemo, useState } from "react";
import { Field, FormikErrors, FormikTouched } from "formik";

import { clsxMerge } from "shared/lib/helpers";
import { BadgeInput } from "shared/ui";
import { TextField } from "shared/ui/ae-user-input";
import { EmailFormI } from "@/components/modules/email-templates-sidebar/content/v2/sections/email-form-section/email-form";
import { BadgeI, BadgeValidationI } from "shared/ui/user-input/badge-input";
import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { DelayedComponent } from "@/components/base/delayed-component";

const EnchancedBadgeInput = BadgeInput(Field);
const EnhancedTextField = TextField();

interface EmailRecipientsSectionPropsI {
  className?: string;
  senderEmail?: string;
  errors?: FormikErrors<EmailFormI>;
  touched?: FormikTouched<EmailFormI>;
  disabled?: boolean;
  isDefaultCcVisible?: boolean;
  isDefaultBccVisible?: boolean;
  to?: BadgeI[] | string[];
  cc?: BadgeI[] | string[];
  bcc?: BadgeI[] | string[];
  onValidateEmails?: (emails: string[]) => Promise<BadgeValidationI>;
}

const EMAIL_VERIFICATION_ERROR_MESSAGE = "Email(s) has not been verified";
const EMAIL_VERIFICATION_SUCCESS_MESSAGE = "Email(s) have been verified";

export const EmailRecipientsSection: FC<EmailRecipientsSectionPropsI> = ({
  className,
  senderEmail,
  errors,
  touched,
  disabled,
  to,
  cc,
  bcc,
  isDefaultCcVisible = false,
  isDefaultBccVisible = false,

  onValidateEmails = () =>
    Promise.resolve({ validValues: [], invalidValues: [] }),
}) => {
  const { contacts, focusedContact } = useAccountDetailsContext();

  const [isCcVisible, setIsCcVisible] = useState(isDefaultCcVisible);
  const [isBccVisible, setIsBccVisible] = useState(isDefaultBccVisible);

  useEffect(() => {
    if (isDefaultCcVisible) {
      setIsCcVisible(true);
    }
  }, [isDefaultCcVisible]);

  useEffect(() => {
    if (isDefaultBccVisible) {
      setIsBccVisible(true);
    }
  }, [isDefaultBccVisible]);

  const emailInputBadgeOptions: BadgeI[] = useMemo(
    () =>
      contacts?.map((c) => ({
        label: c.email || "",
        value: c.email || "",
      })) || [],
    [contacts]
  );

  const defaultEmailBadge: BadgeI[] = useMemo(() => {
    if (!to || !to?.length) {
      return [
        {
          label: focusedContact?.email || "",
          value: focusedContact?.email || "",
        },
      ];
    }

    return (
      to?.map((email) => {
        if (typeof email === "string") {
          return { label: email, value: email };
        }

        return email;
      }) || []
    );
  }, [to, focusedContact]);

  const defaultCcBadges: BadgeI[] = useMemo(
    () =>
      cc?.map((email) => {
        if (typeof email === "string") {
          return { label: email, value: email };
        }

        return email;
      }) || [],
    [cc]
  );

  const defaultBccBadges: BadgeI[] = useMemo(
    () =>
      bcc?.map((email) => {
        if (typeof email === "string") {
          return { label: email, value: email };
        }

        return email;
      }) || [],
    [bcc]
  );

  return (
    <section className={clsxMerge("", className)}>
      <div className="grid grid-cols-2 gap-11">
        <EnhancedTextField
          name="from"
          placeholder=""
          label="From"
          labelClassName="typography-body-4-medium px-1 py-2 text-black"
          inputClassName="bg-white max-h-12"
          inputProps={{ value: senderEmail }}
          disabled
        />

        <div>
          <div className="flex justify-between">
            <h5 className="px-1 py-2 font-medium typography-body-4-medium">
              To
            </h5>

            <div className="flex gap-2">
              {!isCcVisible && (
                <button
                  type="button"
                  className="ae-link"
                  onClick={() => setIsCcVisible(true)}
                >
                  CC
                </button>
              )}
              {!isBccVisible && (
                <button
                  type="button"
                  className="ae-link"
                  onClick={() => setIsBccVisible(true)}
                >
                  BCC
                </button>
              )}
            </div>
          </div>

          <DelayedComponent delay={1000}>
            {({ isReady }) => (
              <EnchancedBadgeInput
                name="to"
                placeholder="Enter email address"
                value={defaultEmailBadge} //component's internal value gets updated through user input, so this basicially acts as initial value
                errors={errors?.to}
                touched={touched?.to}
                className="m-0"
                presetOptions={emailInputBadgeOptions}
                onValidateBadge={onValidateEmails}
                badgeValidationErrorMessage={
                  isReady ? EMAIL_VERIFICATION_ERROR_MESSAGE : undefined
                }
                badgeValidationSuccessMessage={
                  isReady ? EMAIL_VERIFICATION_SUCCESS_MESSAGE : undefined
                }
                badgeValidationErrorClassName="bg-[#FFA95A] text-black px-3 py-1 rounded-lg animate-fadein"
                badgeValidationErrorBorderCss="solid 1px #FFA95A"
                disabled={disabled}
              />
            )}
          </DelayedComponent>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-11">
        {isCcVisible && (
          <DelayedComponent delay={1000}>
            {({ isReady }) => (
              <EnchancedBadgeInput
                name="cc"
                label="CC"
                placeholder="Enter email address"
                value={defaultCcBadges}
                errors={errors?.cc}
                touched={touched?.cc}
                labelContentClassName="typography-body-4-medium"
                presetOptions={emailInputBadgeOptions}
                onValidateBadge={onValidateEmails}
                badgeValidationErrorMessage={
                  isReady ? EMAIL_VERIFICATION_ERROR_MESSAGE : undefined
                }
                badgeValidationSuccessMessage={
                  isReady ? EMAIL_VERIFICATION_SUCCESS_MESSAGE : undefined
                }
                badgeValidationErrorClassName="bg-[#FFA95A] text-black px-3 py-1 rounded-lg animate-fadein"
                badgeValidationErrorBorderCss="solid 1px #FFA95A"
                disabled={disabled}
              />
            )}
          </DelayedComponent>
        )}

        {isBccVisible && (
          <DelayedComponent delay={1000}>
            {({ isReady }) => (
              <EnchancedBadgeInput
                name="bcc"
                label="BCC"
                placeholder="Enter email address"
                value={defaultBccBadges}
                errors={errors?.bcc}
                touched={touched?.bcc}
                labelContentClassName="typography-body-4-medium"
                presetOptions={emailInputBadgeOptions}
                onValidateBadge={onValidateEmails}
                badgeValidationErrorMessage={
                  isReady ? EMAIL_VERIFICATION_ERROR_MESSAGE : undefined
                }
                badgeValidationSuccessMessage={
                  isReady ? EMAIL_VERIFICATION_SUCCESS_MESSAGE : undefined
                }
                badgeValidationErrorClassName="bg-[#FFA95A] text-black px-3 py-1 rounded-lg animate-fadein"
                badgeValidationErrorBorderCss="solid 1px #FFA95A"
                disabled={disabled}
              />
            )}
          </DelayedComponent>
        )}
      </div>
    </section>
  );
};
